import {
  BellOutlined,
  LoadingOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { css } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  App,
  Badge,
  Button,
  Empty,
  Flex,
  Popover,
  Spin,
  Typography,
} from 'antd';
import { useState } from 'react';

import { useMarkOrderNotificationRead } from '@lib/api/hooks/orders/use-mark-order-notification-read';
import { orderNotificationsOptions } from '@lib/api/options/orders';
import { ROUTE_PATHS } from '@modules/route-paths';

const styles = {
  list: css`
    width: min(360px, calc(100vw - 32px));
    max-height: 420px;
    overflow-y: auto;
  `,
  item: css`
    padding: 10px;
    border-bottom: 1px solid var(--ant-color-border-secondary);
  `,
  unreadButton: css`
    &.ant-btn {
      color: var(--ant-color-error);
      border-color: var(--ant-color-error-border);
      background: var(--ant-color-error-bg);
      box-shadow: 0 0 0 4px var(--ant-color-error-bg);
      animation: notification-pulse 1.8s ease-in-out infinite;
    }

    @keyframes notification-pulse {
      50% {
        box-shadow: 0 0 0 8px transparent;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      &.ant-btn {
        animation: none;
      }
    }
  `,
  notificationIcon: css`
    font-size: 24px;
  `,
  popoverRootClassName: css`
    .ant-popover-container {
      min-width: 300px;
      padding-right: 0px;
    }
  `,
};

const OrderNotifications = () => {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const {
    data = [],
    isPending,
    isError,
  } = useQuery(orderNotificationsOptions());
  const { markRead } = useMarkOrderNotificationRead();
  const unread = data.filter((item) => !item.readAt).length;

  const onOpenChange = (next: boolean) => setOpen(next);

  const onSelectNotification = (id: string) => {
    setOpen(false);
    markRead(id).catch(() =>
      message.error('Não foi possível marcar o aviso como lido.'),
    );
  };

  const content = isPending ? (
    <Spin indicator={<LoadingOutlined spin />} />
  ) : isError ? (
    <Typography.Text>Não foi possível carregar os avisos.</Typography.Text>
  ) : data.length === 0 ? (
    <Empty description="Nenhum aviso de pedido" />
  ) : (
    <Flex className={styles.list} vertical>
      {data.map((item) => (
        <Link
          className={styles.item}
          key={item.id}
          to={
            item.perspective === 'purchase'
              ? ROUTE_PATHS.PURCHASE_DETAILS
              : ROUTE_PATHS.SALE_DETAILS
          }
          params={{ orderId: item.orderId }}
          onClick={() => onSelectNotification(item.id)}
        >
          <Flex gap={8} align="flex-start">
            <MessageOutlined className={styles.notificationIcon} />
            <Flex gap={2} vertical>
              <Typography.Text strong={!item.readAt}>
                {item.subject}
              </Typography.Text>
              <Typography.Text type="secondary">{item.message}</Typography.Text>
              <Typography.Text type="secondary">
                {new Date(item.createdAt).toLocaleString('pt-BR')}
              </Typography.Text>
            </Flex>
          </Flex>
        </Link>
      ))}
    </Flex>
  );

  return (
    <Popover
      content={content}
      open={open}
      placement="bottom"
      trigger="click"
      onOpenChange={onOpenChange}
      rootClassName={styles.popoverRootClassName}
    >
      <Badge count={unread} overflowCount={99} size="small">
        <Button
          aria-label={`${unread} notificações não lidas`}
          className={unread > 0 ? styles.unreadButton : undefined}
          icon={<BellOutlined />}
          shape="circle"
        />
      </Badge>
    </Popover>
  );
};

export default OrderNotifications;
